const DATE_RANGE_FIELD = 'dateRange';
const BLANK_ACTIVITY_FIELD = '__blankActivity__';
const EXCEL_BLANK_HEADER = '\u200B';
const STATIC_PROJECT_FIELDS = new Set([
  'projectId',
  'projectName',
  'projectType',
  'projectVertical',
  'projectStatus',
]);

const normalizeText = (value) => (typeof value === 'string' ? value.trim() : '');

const getColumnHeaderText = (column = {}) => (
  normalizeText(column?.header)
  || normalizeText(column?.headerName)
  || normalizeText(column?.label)
  || normalizeText(column?.name)
  || normalizeText(column?.title)
);

const getNormalizedFieldName = (field) => (
  typeof field === 'string' ? field.trim() : ''
);

const isBlankActivityColumn = (column = {}) => {
  const normalizedField = getNormalizedFieldName(column?.field);
  const headerText = getColumnHeaderText(column);
  return column?.type === 'number' && (!normalizedField || !headerText);
};

const getNormalizedBlankValue = (value) => {
  if (value === '' || value === null || value === undefined) return 0;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const normalizeBlankActivityColumn = (tableConfiguration = [], tableData = [], grandTotals = {}) => {
  if (!Array.isArray(tableConfiguration) || tableConfiguration.length === 0) {
    return {
      tableConfiguration,
      tableData,
      grandTotals,
    };
  }

  const normalizedConfiguration = tableConfiguration.map((column) => ({ ...column }));
  const blankActivityIndex = normalizedConfiguration.findIndex(isBlankActivityColumn);

  if (blankActivityIndex === -1) {
    return {
      tableConfiguration: normalizedConfiguration,
      tableData,
      grandTotals,
    };
  }

  const blankColumn = { ...normalizedConfiguration[blankActivityIndex] };
  const sourceField = blankColumn?.field;

  blankColumn.field = BLANK_ACTIVITY_FIELD;
  blankColumn.header = '';
  blankColumn.headerName = '';
  blankColumn.label = '';
  blankColumn.name = '';
  blankColumn.title = '';

  normalizedConfiguration.splice(blankActivityIndex, 1);

  const totalColumnIndex = normalizedConfiguration.findIndex((column) => {
    const fieldName = getNormalizedFieldName(column?.field).toLowerCase();
    return fieldName === 'total';
  });

  const insertIndex = totalColumnIndex >= 0
    ? totalColumnIndex + 1
    : normalizedConfiguration.length;

  normalizedConfiguration.splice(insertIndex, 0, blankColumn);

  const normalizedTableData = Array.isArray(tableData)
    ? tableData.map((row) => {
      const nextRow = { ...row };
      const sourceValue = sourceField === BLANK_ACTIVITY_FIELD
        ? row?.[BLANK_ACTIVITY_FIELD]
        : row?.[sourceField];

      nextRow[BLANK_ACTIVITY_FIELD] = getNormalizedBlankValue(sourceValue);

      return nextRow;
    })
    : tableData;

  const normalizedGrandTotals = {
    ...(grandTotals || {}),
    [BLANK_ACTIVITY_FIELD]: getNormalizedBlankValue(
      grandTotals?.[BLANK_ACTIVITY_FIELD] ?? grandTotals?.[sourceField],
    ),
  };

  return {
    tableConfiguration: normalizedConfiguration,
    tableData: normalizedTableData,
    grandTotals: normalizedGrandTotals,
  };
};

const toFixedTwo = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue.toFixed(2) : value;
};

const formatNumericFields = (row = {}, numericFields = []) => {
  const formattedRow = { ...row };

  numericFields.forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(formattedRow, field)) {
      formattedRow[field] = toFixedTwo(formattedRow[field]);
    }
  });

  return formattedRow;
};

const isExistingTotalRow = (row = {}) => {
  const projectName = normalizeText(row?.projectName).toLowerCase();
  return row?._isFooterRow === true || row?.rowConfiguration?.isFrozen === true || projectName === 'total';
};

const addDateRangeValidationToFilters = (column) => ({
  ...column,
  menuItems: Array.isArray(column?.menuItems)
    ? column.menuItems.map((menuItem) => ({
      ...menuItem,
      items: Array.isArray(menuItem?.items)
        ? menuItem.items.map((item) => {
          if (item?.type !== 'filter') return item;

          const itemField = String(item?.field || '').toLowerCase();
          const itemLabel = String(item?.label || item?.name || '').toLowerCase();
          const isDateRangeFilter = itemField === DATE_RANGE_FIELD
            || (itemLabel.includes('date') && itemLabel.includes('range'));

          if (isDateRangeFilter) {
            return {
              ...item,
              tableUtilityConfig: {
                ...(item?.tableUtilityConfig || {}),
                requiredOnMainApply: true,
                requiredValidationMessage: 'Please set both start date and end date before applying filters.',
              },
            };
          }

          return item;
        })
        : menuItem?.items,
    }))
    : [],
});

const buildEdrActivityPayload = ({
  tableConfiguration = [],
  tableData = [],
  grandTotals,
}) => {
  const {
    tableConfiguration: normalizedTableConfiguration,
    tableData: normalizedTableData,
    grandTotals: normalizedGrandTotals,
  } = normalizeBlankActivityColumn(tableConfiguration, tableData, grandTotals);

  const numericFields = normalizedTableConfiguration
    .filter((column) => column?.type === 'number')
    .map((column) => column.field)
    .filter(Boolean);

  const activityColumns = normalizedTableConfiguration
    .filter((column) => column?.visible !== false)
    .map((column) => column.field)
    .filter((field) => field && !STATIC_PROJECT_FIELDS.has(field));

  const sanitizedTableData = normalizedGrandTotals
    ? normalizedTableData.filter(
      (row) => !isExistingTotalRow(row, activityColumns, numericFields, normalizedGrandTotals),
    )
    : normalizedTableData;

  const formattedTableData = sanitizedTableData.map((row) => formatNumericFields(row, numericFields));

  const tableDataWithFooter = normalizedGrandTotals && formattedTableData.length > 0
    ? [
      ...formattedTableData,
      activityColumns.reduce((result, field) => ({
        ...result,
        [field]: numericFields.includes(field)
          ? toFixedTwo(normalizedGrandTotals[field])
          : (normalizedGrandTotals[field] ?? ''),
      }), {
        projectId: '',
        projectName: 'Total',
        projectType: '',
        projectVertical: '',
        projectStatus: '',
        _isFooterRow: true,
        rowConfiguration: {
          isFrozen: true,
          template: [
            { field: 'projectName', fontWeight: '700' },
            ...activityColumns.map((field) => ({ field, fontWeight: '700' })),
          ],
        },
      }),
    ]
    : formattedTableData;

  return {
    tableConfiguration: normalizedTableConfiguration.map(addDateRangeValidationToFilters),
    tableData: tableDataWithFooter,
    grandTotals: normalizedGrandTotals,
  };
};

export const transformEdrActivityResponse = (response) => {
  if (!response) return;

  const rawTableConfiguration = Array.isArray(response?.data?.tableConfiguration)
    ? response.data.tableConfiguration
    : Array.isArray(response?.data?.data?.tableConfiguration)
      ? response.data.data.tableConfiguration
      : [];

  const rawTableData = response?.data?.tableData
    || response?.data?.data?.tableData
    || [];

  const rawGrandTotals = response?.data?.grandTotals || response?.data?.data?.grandTotals;

  const {
    tableConfiguration: normalizedTableConfiguration,
    tableData: tableDataWithFooter,
    grandTotals,
  } = buildEdrActivityPayload({
    tableConfiguration: rawTableConfiguration,
    tableData: rawTableData,
    grandTotals: rawGrandTotals,
  });

  response.data = {
    ...response.data,
    tableConfiguration: normalizedTableConfiguration,
    tableData: tableDataWithFooter,
    data: {
      ...(response.data?.data || {}),
      tableConfiguration: normalizedTableConfiguration,
      tableData: tableDataWithFooter,
      grandTotals,
    },
    grandTotals,
  };

  const pagination = response.data?.pagination
    || response.data?.paginationConfig
    || response.data?.data?.pagination
    || response.data?.data?.paginationConfig;

  response.meta = {
    ...(response.meta || {}),
    page: pagination?.page ?? response.meta?.page ?? 1,
    limit: pagination?.limit ?? response.meta?.limit ?? 10,
    total:
      pagination?.total
      ?? pagination?.count
      ?? response.meta?.total
      ?? response.data?.tableData?.length
      ?? response.data?.data?.tableData?.length
      ?? 0,
  };
};

export const transformEdrActivityExportResponse = (payload) => {
  if (!payload || typeof payload !== 'object') return payload;

  const {
    tableConfiguration: normalizedTableConfiguration,
    tableData,
    grandTotals,
  } = buildEdrActivityPayload({
    tableConfiguration: Array.isArray(payload?.tableConfiguration) ? payload.tableConfiguration : [],
    tableData: payload?.tableData || [],
    grandTotals: payload?.grandTotals,
  });

  const EXPORT_STATIC_COLUMNS = [
    { field: 'projectId', header: 'Project Id', visible: true, type: 'string', width: 100 },
    { field: 'projectName', header: 'Project Name', visible: true, type: 'string', width: 180 },
    { field: 'projectVertical', header: 'Project Vertical', visible: true, type: 'string', width: 130 },
    { field: 'projectType', header: 'Project Type', visible: true, type: 'string', width: 100 },
    { field: 'projectStatus', header: 'Project Status', visible: true, type: 'string', width: 120 },
  ];

  const visibleColumns = normalizedTableConfiguration.filter(
    (column) => column?.visible !== false,
  );

  const projectNameCol = normalizedTableConfiguration.find((col) => col?.field === 'projectName');

  const restColumns = visibleColumns.filter(
    (col) => !STATIC_PROJECT_FIELDS.has(col?.field),
  );

  const exportTableConfiguration = [
    ...EXPORT_STATIC_COLUMNS.slice(0, 1),
    ...(projectNameCol ? [projectNameCol] : EXPORT_STATIC_COLUMNS.slice(1, 2)),
    ...EXPORT_STATIC_COLUMNS.slice(2),
    ...restColumns,
  ].map((column) => {
    if (column?.field !== BLANK_ACTIVITY_FIELD) return column;

    return {
      ...column,
      header: EXCEL_BLANK_HEADER,
      headerName: EXCEL_BLANK_HEADER,
      label: EXCEL_BLANK_HEADER,
      name: EXCEL_BLANK_HEADER,
      title: EXCEL_BLANK_HEADER,
    };
  });

  const exportTableData = tableData.map((row) => {
    const trimmedProjectName = typeof row?.projectName === 'string' ? row.projectName.trim() : row?.projectName;

    if (row?._isFooterRow) return { ...row, projectId: '', projectName: trimmedProjectName };

    const rawId = row?.projectId;
    if (rawId == null || rawId === '') return { ...row, projectId: rawId, projectName: trimmedProjectName };

    const parsed = parseInt(parseFloat(String(rawId)), 10);
    const idString = Number.isFinite(parsed) ? String(parsed) : String(rawId);
    return { ...row, projectId: `\u200C${idString}`, projectName: trimmedProjectName };
  });

  return {
    ...payload,
    tableConfiguration: exportTableConfiguration,
    tableData: exportTableData,
    grandTotals,
  };
};

export const syncPaginationFromResponse = (setPagination, response) => {
  setPagination((previousPagination) => ({
    ...previousPagination,
    page: response?.meta?.page ?? previousPagination.page,
    limit: response?.meta?.limit ?? previousPagination.limit,
    total: response?.meta?.total ?? previousPagination.total,
  }));
};
